import React, { useState } from 'react';
import { SparklesIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { lienAchat } from '../../utils/achats';

/**
 * Aide à la candidature par l'IA (achat à l'unité « candidature_ia »).
 * Le serveur relit l'appel à projets et les données du candidat ; l'IA propose, le candidat
 * relit puis choisit « Utiliser » (les champs sont remplis, rien n'est envoyé).
 * Une proposition = une unité achetée (rendue par le serveur si l'IA échoue).
 */
const AiCandidature = ({ callId, formData, onUse }) => {
  const [etat, setEtat] = useState('pret');
  const [proposition, setProposition] = useState(null);
  const [message, setMessage] = useState('');
  const [achatRequis, setAchatRequis] = useState(false);

  const demander = async () => {
    // Une proposition utilise une aide achetée : on prévient avant (sans achat, rien n'est pris)
    if (!window.confirm('Une proposition utilise une aide à la candidature achetée. Si vous n\'en avez pas, nous vous proposerons d\'en acheter une. Continuer ?')) return;
    setEtat('chargement');
    setMessage('');
    setAchatRequis(false);
    try {
      const { data } = await api.post('/ai/candidature', {
        callId,
        input: {
          message: formData.message,
          experience: formData.additionalInfo.experience,
          motivation: formData.additionalInfo.motivation,
          timeline: formData.additionalInfo.timeline,
          budget: formData.additionalInfo.budget,
        },
      }, { timeout: 70000 });
      setProposition(data?.proposal || null);
      setEtat('pret');
    } catch (err) {
      const body = err.response?.data || {};
      if (err.response?.status === 402 && body.code === 'ACHAT_REQUIS') setAchatRequis(true);
      setMessage(body.message || 'L\'IA n\'a pas pu proposer de texte. Réessayez.');
      setEtat('pret');
    }
  };

  // La proposition reste affichée : elle a été payée, le candidat peut y revenir
  const utiliser = () => onUse(proposition);

  return (
    <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 space-y-3">
      <div className="flex items-start gap-3">
        <SparklesIcon className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" aria-hidden="true" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-amber-900">Rédiger ma candidature avec l'IA</p>
          <p className="text-sm text-amber-800">
            L'IA relie votre projet (business plan, projets publiés, chiffres réels) aux critères de cet appel.
            Achat à l'unité : une proposition par achat. Vous relisez avant d'envoyer.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={demander}
          hidden={Boolean(proposition)}
          disabled={etat === 'chargement'}
          className="px-4 py-2 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 disabled:opacity-60"
        >
          {etat === 'chargement' ? 'Rédaction en cours…' : 'Proposer ma candidature'}
        </button>
        {achatRequis && (
          <a href={lienAchat('candidature_ia')} className="px-4 py-2 border border-amber-600 text-amber-800 text-sm rounded-lg hover:bg-amber-100">
            Acheter une aide à la candidature
          </a>
        )}
      </div>
      {message && <p className="text-sm text-red-700">{message}</p>}
      {proposition && (
        <div className="bg-white border border-amber-200 rounded-lg p-3 space-y-2">
          <p className="text-sm font-medium text-gray-900">Proposition de l'IA</p>
          <p className="text-sm text-gray-700 whitespace-pre-line">{proposition.message}</p>
          {proposition.experience && <p className="text-sm text-gray-700"><strong>Expérience :</strong> {proposition.experience}</p>}
          {proposition.motivation && <p className="text-sm text-gray-700"><strong>Motivation :</strong> {proposition.motivation}</p>}
          {proposition.timeline && <p className="text-sm text-gray-700"><strong>Calendrier :</strong> {proposition.timeline}</p>}
          {proposition.budget && <p className="text-sm text-gray-700"><strong>Budget :</strong> {proposition.budget}</p>}
          {Array.isArray(proposition.tips) && proposition.tips.length > 0 && (
            <ul className="list-disc pl-5 text-sm text-amber-800">
              {proposition.tips.map((t) => <li key={t}>{t}</li>)}
            </ul>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={utiliser} className="px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700">Utiliser</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiCandidature;
